// This js listens for filter events, fetches the item data, and renders the content
// in an ItemBuyBox

import addStartupScript from 'mex_components/app/scripts/events/addStartupScript';
import { handleEvent } from 'mex_components/app/scripts/tracking/events';
import itemOffers from '../templates/item_offers.mustache';
import { multipleItemFetch } from './item_fetch';
import setupAddToCart from './add_to_cart';
import setupStraightEnrollment from './straight_enrollment';
import toggleClass from 'mex_components/app/scripts/elements/toggleClass';
import { track } from 'mex_components/app/scripts/tracking/newrelic';

const WRAPPER_SELECTOR = '.item-buy-box';
const FILTERS_SELECTOR = '.item-buy-box__filters';
const DROPDOWN_FILTER_SELECTOR = `${FILTERS_SELECTOR} > .dropdown`;
const NO_FILTER_SELECTOR = `${FILTERS_SELECTOR} > .no-filter`;
const SUBSCRIPTION_FILTER_SELECTOR = `${FILTERS_SELECTOR} > .buy-box-subscription-filter`;
let wrapper;
export let userSubscriptions;

/**
 * Executes the item fetch for each item id in wrapper.dataset.itemIds. If a subscription id
 * is present, will render offers with a matching subscription id; otherwise will render offers
 * with no optional subscription.
 */
const fetchItems = async () => {
  const itemIds = wrapper.dataset.itemIds.split(', ');
  const subscriptionId = wrapper.dataset.subscriptionId;
  let filteredData = { offers: [] };
  const data = await multipleItemFetch('items-pr', itemIds);

  filteredData.offers = data.offers.filter((item) => {
    // filter the subscription offers in our out, depending on if a subscription id is present
    return subscriptionId
      ? item.subscription_program_id === subscriptionId
      : !item.subscription_program_id;
  });

  // combine offers that are at the same day-of-week and time
  // for optional subscription only
  if (subscriptionId) {
    const uniqueSubOffers = new Map();

    filteredData.offers.map((offer) => {
      const key = `${offer.formatted_days} ${offer.formatted_times}`;
      // store the offer under the key if the key doesn't exist or if the already-stored offer
      // at the same key has a next session *after* the current offer's next session
      if (
        !uniqueSubOffers.has(key) ||
        uniqueSubOffers.get(key)?.next_session_time > offer.next_session_time
      ) {
        uniqueSubOffers.set(key, offer);
      }
    });

    filteredData.offers = [...uniqueSubOffers.values()];
  }

  // hide the extra item details by default for all NSP topic pages for now
  filteredData.hide_details = true;

  renderOffers(itemIds, filteredData);

  // Set up tracking for enroll buttons
  [
    ...wrapper.querySelectorAll(
      '[class*="js-track"], [data-track], button, label, a'
    )
  ].forEach((el) => {
    el.addEventListener('click', handleEvent);
  });
};

/**
 * Event handler for when the dropdown changes. Expecting payload to
 * include data.value where the value is comma-separated string of item ids
 */
const handleDropdownFilterChange = ({ detail: { data } }) => {
  if (!data || !data.value) return;

  wrapper.dataset.itemIds = data.value;

  fetchItems();
};

/**
 * Event handler for when the subscription filter changes. Expecting payload to
 * include data.itemIds and an optional data.subscriptionId
 */
const handleSubscriptionFilterChange = ({ detail: { data } }) => {
  if (!data || !data.itemIds) return;

  wrapper.dataset.itemIds = data.itemIds;
  wrapper.dataset.subscriptionId = data.subscriptionId;

  fetchItems();
};

/**
 * If > 3 offers, will only show the first 3 on tablet/desktop and will
 * show the others after clicking a "Show All Class Offerings" link
 *
 * @param {Element} wrapper Wrapper element containing the offers
 */
export const hideExtraOffers = (wrapper) => {
  const allOffers = wrapper.querySelectorAll('.item-offer');
  const fourthOffer = allOffers[3];
  const mediaQuery = window.matchMedia('(min-width: 600px)');

  if (fourthOffer && mediaQuery.matches) {
    [...allOffers].forEach((offer, index) => {
      if (index > 2) toggleClass(offer, 'hidden', true);
    });
    const viewAllLink = document.createElement('a');
    viewAllLink.innerHTML = 'See All Class Offerings';
    viewAllLink.setAttribute('class', 'link link--show-offers');
    fourthOffer.parentNode.insertBefore(viewAllLink, fourthOffer);

    viewAllLink.addEventListener('click', (e) => {
      e.target.style.display = 'none';
      [...allOffers].forEach((offer) => {
        toggleClass(offer, 'hidden', false);
      });
    });
  }
};

/**
 * Initializes the filters in the buy box
 */
const initializeFilters = () => {
  const filtersWrapper = wrapper.querySelector(FILTERS_SELECTOR);
  const outOfStockWrapper = wrapper.querySelector(
    '.item-buy-box__out-of-stock-content'
  );

  // if a filter hasn't been set, show out-of-stock message
  if (!filtersWrapper) {
    toggleClass(outOfStockWrapper, 'hidden', false);
    return;
  }

  const noFilter = wrapper.querySelector(NO_FILTER_SELECTOR);
  const dropdownFilter = wrapper.querySelector(DROPDOWN_FILTER_SELECTOR);
  const subscriptionFilter = wrapper.querySelector(
    SUBSCRIPTION_FILTER_SELECTOR
  );

  if (noFilter) {
    setupNoFilter(noFilter);
  } else if (dropdownFilter) {
    document.addEventListener('dropdown.change', handleDropdownFilterChange);
  } else if (subscriptionFilter) {
    document.addEventListener(
      'subscriptionFilter.change',
      handleSubscriptionFilterChange
    );
  }
};

/**
 * Fetches the parent items on page load. Expects the component to have an
 * ItemId dataset with comma-separated item ids.
 *
 * @param {Element} filter filter element
 */
const setupNoFilter = (filter) => {
  if (!filter || !filter.dataset.itemIds) return;

  wrapper.dataset.itemIds = filter.dataset.itemIds;
  fetchItems();
};

/**
 * Renders the offers. If no data, will show the out-of-stock content
 *
 * @param {String} itemIds Array of parent item IDs
 * @param {Object} data All data required by itemOffers
 */
const renderOffers = (itemIds, data) => {
  const offersWrapper = wrapper.querySelector('.item-buy-box__offers');
  const outOfStockWrapper = wrapper.querySelector(
    '.item-buy-box__out-of-stock-content'
  );

  // only show first 3 offerings
  if (data && data.offers && data.offers.length > 0) {
    offersWrapper.innerHTML = itemOffers(data);
    hideExtraOffers(offersWrapper);

    // show offers and hide stock out message
    toggleClass(outOfStockWrapper, 'hidden', true);
    toggleClass(offersWrapper, 'hidden', false);
    setupAddToCart();
    setupStraightEnrollment();
  } else {
    // hide offers and show stock out message
    toggleClass(outOfStockWrapper, 'hidden', false);
    toggleClass(offersWrapper, 'hidden', true);

    // send stock out event to newrelic
    track('No offers found', { itemIds });
  }
};

export function setup() {
  wrapper = document.querySelector(WRAPPER_SELECTOR);
  // return quickly if we don't have an ItemBuyBox
  if (!wrapper) return;

  userSubscriptions = wrapper.dataset.userSubscriptions.split(',');

  initializeFilters();
}

addStartupScript(setup);
